import axios from 'axios';
import { getConfig } from '../config';
import type { CMWorkflowItem, CMWorkflowStatus, CMRequestReason, SkuCpiiMapping } from '../models/CatalogManagement';

export interface CmOperation {
  operator: string;
  reason?: CMRequestReason;
  message?: string;
}

export interface SubmitQAReviewRequest {
  gpi: string;
  cppi: string;
  qaResults: Record<
    string,
    {
      qaOperator: string;
      qaStatus: 'pass' | 'fail';
      qaVerifiedAt: string;
      message?: string | undefined;
    } | null
  >;
}

export interface CreateCMWorkflowRequest {
  gpi: string;
  cppi: string;
  skuCpiiMappings?: SkuCpiiMapping[];
  cmStatus: CMWorkflowStatus;
  wave: string;
  cmOperation?: CmOperation;
}

export interface GetAllCMWorkflowsResponse {
  workflows: CMWorkflowItem[];
  lastEvaluatedKey?: string;
  hasNextPage: boolean;
}

export class CMWorkflowClient {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = getConfig().apiUrl;
  }

  async getCMWorkflowByParams(params: { gpi?: string; cppi?: string; cmStatus?: string }): Promise<CMWorkflowItem[]> {
    const url = `${this.baseUrl}/cmWorkflow?${new URLSearchParams(params).toString()}`;
    console.info('🔍 Fetching CM workflow by params:', url);

    try {
      const response = await axios.get(url);
      console.info('✅ CM workflow fetched successfully:', response.data);

      if (Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response.data?.items)) {
        return response.data.items;
      } else if (Array.isArray(response.data?.workflows)) {
        return response.data.workflows;
      } else {
        console.warn('⚠️ Unexpected response format, returning empty array');
        return [];
      }
    } catch (error) {
      console.error('❌ Failed to fetch CM workflow:', error);
      throw error;
    }
  }

  async getAllCMWorkflows(): Promise<GetAllCMWorkflowsResponse> {
    const url = `${this.baseUrl}/cmWorkflows/?limit=1000`;

    console.info('🔍 Fetching all CM workflows (no filters):', url);

    try {
      const response = await axios.get(url);
      console.info('📥 Raw API response:', response.data);

      let workflows: CMWorkflowItem[] = [];
      if (Array.isArray(response.data)) {
        workflows = response.data;
      } else if (Array.isArray(response.data?.items)) {
        workflows = response.data.items;
      } else if (Array.isArray(response.data?.workflows)) {
        workflows = response.data.workflows;
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
      }

      console.info(`✅ Fetched ${workflows.length} CM workflows`);
      return {
        workflows,
        lastEvaluatedKey: response.data?.lastEvaluatedKey,
        hasNextPage: Boolean(response.data?.lastEvaluatedKey),
      };
    } catch (error) {
      console.error('❌ Failed to fetch all CM workflows:', error);
      throw error;
    }
  }

  async createCMWorkflow(request: CreateCMWorkflowRequest): Promise<CMWorkflowItem> {
    const url = `${this.baseUrl}/cmWorkflow`;
    console.info('🌐 CMWorkflowClient.createCMWorkflow() called');
    console.info('🌐 URL:', url);
    console.info('🌐 Request type:', typeof request);
    console.info('🌐 Request keys:', Object.keys(request));
    console.info('🌐 Request payload (stringified):', JSON.stringify(request, null, 2));

    try {
      console.info('⚡ About to call axios.post...');
      const response = await axios.post<CMWorkflowItem>(url, request, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.info('✅ CM workflow created successfully. Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create CM workflow:', error);
      throw error;
    }
  }

  async submitQAReview(request: SubmitQAReviewRequest): Promise<CMWorkflowItem> {
    const url = `${this.baseUrl}/cmWorkflow/qaResults`;
    console.info('📋 CMWorkflowClient.submitQAReview() called');
    console.info('📋 URL:', url);
    console.info('📋 Request payload:', JSON.stringify(request, null, 2));

    try {
      const response = await axios.patch<CMWorkflowItem>(url, request, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.info('✅ QA review submitted successfully. Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to submit QA review:', error);
      throw error;
    }
  }

  async deleteCMWorkflow(gpi: string, cppi: string): Promise<void> {
    const url = `${this.baseUrl}/cmWorkflow/${gpi}/${cppi}`;
    console.info('🗑️ CMWorkflowClient.deleteCMWorkflow() called');
    console.info('🗑️ URL:', url);

    try {
      await axios.delete(url);
      console.info('✅ CM workflow deleted successfully');
    } catch (error) {
      console.error('❌ Failed to delete CM workflow:', error);
      throw error;
    }
  }
}

export const cmWorkflowClient = new CMWorkflowClient();
